import { getCountries } from '../../common/countryData'
import * as React from 'react'
import useSWR from 'swr'
import './externalSiteList.scss'
import { IdAndTitle, ExternalLocation, ShownCountryWithLocations } from './types'
import LocationListTable from './LocationListTable'

interface Props {
	country: string
	hash: string
}

const ExternalSiteList = ({ country, hash }: Props) => {
	const { data, error } = useSWR<
		{
			status: string
			data: {
				countries: string[]
				sites: ExternalLocation[]
				categories: IdAndTitle[]
			}
		},
		Error
	>(`/external/siteList/${country}/${hash}`, {
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
	})
	const locations = data?.data?.sites ?? []
	const countries = getCountries()

	const isLoading = !data && !error

	const allCategories = data?.data?.categories ?? []

	const [selectedCountry, setSelectedCountry] = React.useState(
		// select the first country in the parameters as default
		country.substring(0, 2),
	)

	const isCountrySelected = (countryCode: string): boolean => selectedCountry === countryCode

	const [allCategoriesShown, setAllCategoriesShown] = React.useState(false)

	const [selectedCategoryIds, setSelectedCategories] = React.useState<number[]>([])

	const getNumberOfLocationsInCategoryInCountry = (category: IdAndTitle) =>
		locations
			.filter((location) => location.country === selectedCountry)
			.filter((location) => location.categoryIds.some((cid) => cid === category.id)).length

	const toggleCategory = (id: number) => {
		if (selectedCategoryIds.includes(id)) {
			setSelectedCategories(selectedCategoryIds.filter((category) => category !== id))
		} else {
			setSelectedCategories([...selectedCategoryIds, id])
		}
	}

	const showAllCategories = () => {
		setAllCategoriesShown(false)
		setSelectedCategories([])
	}

	const locationsMatchingCategories =
		selectedCategoryIds.length === 0
			? locations
			: locations.filter((location) => location.categoryIds.some((cid) => selectedCategoryIds.includes(cid)))

	const shownCountriesWithLocations: ShownCountryWithLocations[] = (data?.data?.countries ?? []).map(
		(countryCode) => ({
			countryCode,
			countryName: countries.find((c) => c.code === countryCode)?.name ?? countryCode,
			locations: locationsMatchingCategories
				.filter((location) => location.country === countryCode)
				.sort((a, b) => a.title.localeCompare(b.title)),
		}),
	)

	const showCountries = shownCountriesWithLocations.length > 1

	return (
		<div className="external-site-list">
			<div className="external-site-list__hero">
				<h1 className="external-site-list__title">Site lists</h1>
				<span className="external-site-list__subtitle">All sites available through the Readpeak platform</span>

				{showCountries && (
					<div className="external-site-list__countries">
						{shownCountriesWithLocations.map((shownCountry) => (
							<a
								key={shownCountry.countryCode}
								onClick={() => setSelectedCountry(shownCountry.countryCode)}
								className={`external-site-list__country-link ${
									isCountrySelected(shownCountry.countryCode)
										? 'external-site-list__country-link--selected'
										: ''
								}`}
							>
								{shownCountry.countryName}
							</a>
						))}
					</div>
				)}

				{false && /* hide categories temporarily */ !isLoading && (
					<div className="external-site-list__categories">
						{!allCategoriesShown && (
							<button className="external-site-list__button" onClick={() => setAllCategoriesShown(true)}>
								Show filters
							</button>
						)}

						{allCategoriesShown && (
							<>
								<button
									className={`external-site-list__button ${
										selectedCategoryIds.length === 0 ? '' : 'external-site-list__button--selected'
									}`}
									onClick={() => showAllCategories()}
								>
									Close filters
								</button>
								<br />
								<br />
								{allCategories.map((category) => (
									<button
										key={category.id}
										className={`external-site-list__button ${
											selectedCategoryIds.includes(category.id)
												? 'external-site-list__button--selected'
												: ''
										}`}
										onClick={() => toggleCategory(category.id)}
									>
										{category.title}
										<span className="external-site-list__location-count">
											{getNumberOfLocationsInCategoryInCountry(category)}
										</span>
									</button>
								))}
							</>
						)}
					</div>
				)}
			</div>
			<div className="external-site-list__container">
				{isLoading ? (
					<div className="external-site-list__loading">Loading, please wait ...</div>
				) : (
					<LocationListTable
						shownCountriesWithLocations={shownCountriesWithLocations}
						selectedCountry={selectedCountry}
					/>
				)}
			</div>
		</div>
	)
}
export default ExternalSiteList
